import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Plus, Trash2, Save, CheckCircle2, Circle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, Field, Input, inputCls, Spinner, ConfirmDeleteDialog, PageGuide } from "../../components/ui-kit";
import { KNOWN_PAGES } from "../../academy/pageKeys";

const err = (e) => formatApiErrorDetail(e.response?.data?.detail);
const linesToArray = (text) => text.split("\n").map((line) => line.trim()).filter(Boolean);
const arrayToLines = (arr) => (arr || []).join("\n");

const emptyForm = (title = "") => ({
  title,
  guide: { purpose: "", when_to_use: "", buttons: [], procedure: "", errors: "", tips: "" },
  video: { url: "", title: "" },
  tour_steps: [],
  active: true,
});

function fromApi(item) {
  return {
    title: item.title || "",
    guide: {
      purpose: item.guide?.purpose || "",
      when_to_use: item.guide?.when_to_use || "",
      buttons: item.guide?.buttons || [],
      procedure: arrayToLines(item.guide?.procedure),
      errors: arrayToLines(item.guide?.errors),
      tips: arrayToLines(item.guide?.tips),
    },
    video: { url: item.video?.url || "", title: item.video?.title || "" },
    tour_steps: item.tour_steps || [],
    active: item.active !== false,
  };
}

function toApi(form) {
  return {
    title: form.title,
    guide: {
      purpose: form.guide.purpose,
      when_to_use: form.guide.when_to_use,
      buttons: form.guide.buttons.filter((b) => b.label.trim() || b.meaning.trim()),
      procedure: linesToArray(form.guide.procedure),
      errors: linesToArray(form.guide.errors),
      tips: linesToArray(form.guide.tips),
    },
    video: form.video,
    tour_steps: form.tour_steps.filter((s) => s.selector.trim()),
    active: form.active,
  };
}

export default function AcademyAdmin() {
  const [items, setItems] = useState(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => api.get("/admin/academy").then(({ data }) => setItems(data.items)).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, []);

  const knownByKey = useMemo(() => Object.fromEntries(KNOWN_PAGES.map((p) => [p.page_key, p.title])), []);
  const configuredKeys = useMemo(() => new Set((items || []).map((i) => i.page_key)), [items]);
  const allPageOptions = useMemo(() => {
    const extra = (items || []).filter((i) => !knownByKey[i.page_key]).map((i) => ({ page_key: i.page_key, title: i.title }));
    return [...KNOWN_PAGES, ...extra];
  }, [items, knownByKey]);

  const selectPage = async (pageKey) => {
    setSelectedKey(pageKey);
    if (!pageKey) { setForm(null); return; }
    try {
      const { data } = await api.get(`/admin/academy/${pageKey}`);
      setForm(fromApi(data.item));
    } catch {
      setForm(emptyForm(knownByKey[pageKey] || pageKey));
    }
  };

  const save = async () => {
    if (!selectedKey) return;
    if (!form.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/academy/${selectedKey}`, toApi(form));
      toast.success("Contenuto salvato");
      await load();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/academy/${selectedKey}`);
      toast.success("Contenuto eliminato");
      setDeleting(false);
      setForm(null);
      setSelectedKey("");
      await load();
    } catch (e) { toast.error(err(e)); }
    finally { setDeleteBusy(false); }
  };

  const updateGuide = (patch) => setForm((f) => ({ ...f, guide: { ...f.guide, ...patch } }));
  const updateVideo = (patch) => setForm((f) => ({ ...f, video: { ...f.video, ...patch } }));

  const addButton = () => updateGuide({ buttons: [...form.guide.buttons, { label: "", meaning: "" }] });
  const updateButton = (i, patch) => updateGuide({ buttons: form.guide.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });
  const removeButton = (i) => updateGuide({ buttons: form.guide.buttons.filter((_, idx) => idx !== i) });

  const addStep = () => setForm((f) => ({ ...f, tour_steps: [...f.tour_steps, { selector: "", title: "", body: "" }] }));
  const updateStep = (i, patch) => setForm((f) => ({ ...f, tour_steps: f.tour_steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  const removeStep = (i) => setForm((f) => ({ ...f, tour_steps: f.tour_steps.filter((_, idx) => idx !== i) }));

  if (items === null) return <Spinner />;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">DF Academy</p>
        <h1 className="font-heading text-3xl font-bold text-navy">Smart Help e AI Guided Learning</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">Scrivi qui la guida contestuale, il link della videoguida e i passi del tour per ogni pagina dell'app. Le modifiche sono visibili subito ai clienti, senza bisogno di un deploy.</p>
      </div>

      <PageGuide
        title="Guida rapida: DF Academy"
        summary="Questa è la guida che vedranno i clienti dentro l'app, pagina per pagina: quello che scrivi qui compare subito, senza bisogno di un deploy."
        goals={["Scrivere la guida contestuale, la videoguida e il tour guidato che i clienti vedono in ogni pagina dell'app."]}
        steps={[
          "Scegli una pagina dall'elenco a sinistra (le pagine già configurate hanno il segno di spunta verde).",
          "Compila “A cosa serve”, “Quando usarla”, il significato dei pulsanti e la procedura passo passo.",
          "Se disponibile, aggiungi il link a una videoguida (YouTube, Vimeo o un file .mp4).",
          "Attiva “Pubblicata” e premi “Salva” per rendere la guida visibile ai clienti.",
        ]}
        testid="academy-guide"
      />

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <Card className="h-fit p-4">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-400">Pagine</h2>
          <div className="mt-3 space-y-1">
            {allPageOptions.map((p) => (
              <button
                key={p.page_key}
                type="button"
                onClick={() => selectPage(p.page_key)}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${selectedKey === p.page_key ? "bg-navy text-white" : "text-navy hover:bg-slate-100"}`}
                data-testid={`academy-page-${p.page_key}`}
              >
                {configuredKeys.has(p.page_key) ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> : <Circle className="h-4 w-4 shrink-0 text-slate-300" />}
                <span className="min-w-0 flex-1 truncate">{p.title}</span>
              </button>
            ))}
          </div>
        </Card>

        {!form ? (
          <Card className="grid place-items-center p-10 text-center text-slate-400">
            <GraduationCap className="mb-3 h-9 w-9 opacity-50" />
            Scegli una pagina dall'elenco per scrivere o modificare la sua guida.
          </Card>
        ) : (
          <div className="space-y-5">
            <Card className="p-5">
              <Field label="Titolo pagina (mostrato nella guida)"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <div className="mt-4 grid gap-4">
                <Field label="A cosa serve"><textarea className={inputCls} rows={2} value={form.guide.purpose} onChange={(e) => updateGuide({ purpose: e.target.value })} /></Field>
                <Field label="Quando usarla"><textarea className={inputCls} rows={2} value={form.guide.when_to_use} onChange={(e) => updateGuide({ when_to_use: e.target.value })} /></Field>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-navy">Significato dei pulsanti</h3>
                  <button type="button" onClick={addButton} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"><Plus className="h-3.5 w-3.5" /> Aggiungi</button>
                </div>
                <div className="mt-2 space-y-2">
                  {form.guide.buttons.map((b, i) => (
                    <div key={i} className="flex flex-col gap-2 rounded-lg border border-hairline p-2 sm:flex-row">
                      <Input className="sm:w-48" placeholder="Nome pulsante" value={b.label} onChange={(e) => updateButton(i, { label: e.target.value })} />
                      <Input placeholder="Cosa fa" value={b.meaning} onChange={(e) => updateButton(i, { meaning: e.target.value })} />
                      <button type="button" onClick={() => removeButton(i)} className="shrink-0 self-end rounded-lg p-2 text-red-600 hover:bg-red-50 sm:self-auto" aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {form.guide.buttons.length === 0 && <p className="text-xs text-slate-400">Nessun pulsante descritto ancora.</p>}
                </div>
              </div>

              <div className="mt-5 grid gap-4">
                <Field label="Procedura (un passo per riga)"><textarea className={inputCls} rows={4} value={form.guide.procedure} onChange={(e) => updateGuide({ procedure: e.target.value })} /></Field>
                <Field label="Errori comuni (uno per riga)"><textarea className={inputCls} rows={3} value={form.guide.errors} onChange={(e) => updateGuide({ errors: e.target.value })} /></Field>
                <Field label="Consigli (uno per riga)"><textarea className={inputCls} rows={3} value={form.guide.tips} onChange={(e) => updateGuide({ tips: e.target.value })} /></Field>
              </div>
            </Card>

            <Card className="p-5">
              <h3 className="font-heading text-lg font-semibold text-navy">Videoguida</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Titolo del video"><Input value={form.video.title} onChange={(e) => updateVideo({ title: e.target.value })} /></Field>
                <Field label="Link (YouTube, Vimeo o file .mp4)"><Input value={form.video.url} onChange={(e) => updateVideo({ url: e.target.value })} placeholder="https://..." /></Field>
              </div>
            </Card>

            <Card className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-semibold text-navy">Tour guidato</h3>
                <button type="button" onClick={addStep} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"><Plus className="h-3.5 w-3.5" /> Aggiungi passo</button>
              </div>
              <p className="mt-1 text-xs text-slate-400">Il "selettore" è l'attributo data-testid dell'elemento da illuminare, es. <code className="rounded bg-slate-100 px-1">[data-testid='ricetta-new']</code>.</p>
              <div className="mt-3 space-y-3">
                {form.tour_steps.map((s, i) => (
                  <div key={i} className="rounded-lg border border-hairline p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-400">Passo {i + 1}</span>
                      <button type="button" onClick={() => removeStep(i)} className="rounded-lg p-1.5 text-red-600 hover:bg-red-50" aria-label="Rimuovi passo"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <Input placeholder="Selettore, es. [data-testid='ricetta-new']" value={s.selector} onChange={(e) => updateStep(i, { selector: e.target.value })} />
                      <Input placeholder="Titolo del passo" value={s.title} onChange={(e) => updateStep(i, { title: e.target.value })} />
                    </div>
                    <textarea className={`${inputCls} mt-2`} rows={2} placeholder="Testo del passo" value={s.body} onChange={(e) => updateStep(i, { body: e.target.value })} />
                  </div>
                ))}
                {form.tour_steps.length === 0 && <p className="text-xs text-slate-400">Nessun passo del tour ancora configurato.</p>}
              </div>
            </Card>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Pubblicata (visibile ai clienti)</label>
              <div className="ml-auto flex gap-2">
                {configuredKeys.has(selectedKey) && <Button variant="danger" onClick={() => setDeleting(true)}><Trash2 className="h-4 w-4" /> Elimina</Button>}
                <Button variant="success" loading={saving} onClick={save}><Save className="h-4 w-4" /> Salva</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleting}
        title="Eliminare questo contenuto?"
        description="La pagina tornerà a non avere alcuna guida, videoguida o tour finché non ne scrivi una nuova."
        busy={deleteBusy}
        onCancel={() => setDeleting(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
